import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react'
import { LayoutChangeEvent, NativeScrollEvent, NativeSyntheticEvent, ScrollView, View, ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { Display } from '../../atoms/display/Display'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { BodyTextSize, DisplaySize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

const Row = styled.View<{ $isScrollable: boolean; $isPill: boolean }>(({ theme, $isScrollable, $isPill }) => ({
  flexDirection: 'row',
  gap: $isPill ? theme.spacing['8px'] : theme.spacing['24px'],
  ...($isScrollable && {
    alignSelf: 'flex-start',
    flexShrink: 0,
    flexWrap: 'nowrap',
  }),
}))

const TabItem = styled(PressableOpacity)<{ $preventShrink: boolean; $isPill: boolean; $isActive: boolean }>(({
  theme,
  $preventShrink,
  $isPill,
  $isActive,
}) => {
  const { container, optionSelected } = theme.colors.components.segmentedToggle
  return {
    flexDirection: 'row',
    ...($isPill
      ? {
          alignItems: 'center',
          paddingVertical: theme.spacing['8px'],
          paddingHorizontal: theme.spacing['24px'],
          borderRadius: '100px',
          borderWidth: '1px',
          backgroundColor: $isActive ? optionSelected.background : container.background,
          borderColor: $isActive ? optionSelected.background : container.border,
        }
      : { paddingBottom: theme.spacing['8px'] }),
    ...($preventShrink && { flexShrink: 0 }),
  }
})

const DisplayTabText = styled(Display)<{ isActive: boolean; $preserveCase: boolean }>(
  ({ theme, isActive, $preserveCase }) => ({
    color: isActive ? theme.colors.textActive : theme.colors.textSecondary,
    textTransform: $preserveCase ? 'none' : 'uppercase',
  }),
)

const StandardTabText = styled(BodyText)<{
  isActive: boolean
  activeColor?: string
  inactiveColor?: string
  hasBadge?: boolean
  $isPill?: boolean
}>(({ theme, isActive, activeColor, inactiveColor, hasBadge, $isPill }) => ({
  // Pill labels use the same tokens as the pill fills so the text stays readable in every theme
  color: $isPill
    ? isActive
      ? theme.colors.components.segmentedToggle.optionSelected.text
      : theme.colors.components.segmentedToggle.option.text
    : isActive
      ? (activeColor ?? theme.colors.textActive)
      : (inactiveColor ?? theme.colors.textSecondary),
  paddingVertical: $isPill ? 0 : theme.spacing['8px'],
  paddingLeft: $isPill ? 0 : theme.spacing['8px'],
  paddingRight: hasBadge || $isPill ? 0 : theme.spacing['8px'],
  fontWeight: 500,
  lineHeight: '24px',
}))

const TabBadge = styled.View<{ badgeCountColor?: string }>(({ theme, badgeCountColor }) => ({
  alignSelf: 'center',
  marginLeft: theme.spacing['4px'],
  minWidth: theme.spacing['16px'],
  height: theme.spacing['16px'],
  paddingHorizontal: theme.spacing['4px'],
  borderRadius: theme.spacing['6px'],
  borderWidth: 1.5,
  borderColor: theme.colors.textInverse,
  backgroundColor: badgeCountColor ?? theme.colors.iconError,
  alignItems: 'center',
  justifyContent: 'center',
}))

const ActiveIndicator = styled.View<{ backgroundColor?: string; height?: string; borderRadius?: string }>(
  ({ theme, backgroundColor, height, borderRadius }) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: height || '4px',
    backgroundColor: backgroundColor || theme.colors.backgroundActive,
    borderTopLeftRadius: borderRadius || theme.spacing['32px'],
    borderTopRightRadius: borderRadius || theme.spacing['32px'],
  }),
)

const TabWrapper = styled(View)<{ fullHeight: boolean; $preventShrink: boolean }>(({ fullHeight, $preventShrink }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  position: 'relative',
  ...(fullHeight && { height: '100%' }),
  ...($preventShrink && { flexShrink: 0 }),
}))

export enum IndicatorVariant {
  UNDER_LABEL = 'UNDER_LABEL',
  BOTTOM_BAR = 'BOTTOM_BAR',
  /** Each tab is a rounded pill. The active tab gets a solid fill instead of an indicator bar. */
  PILL = 'PILL',
}

export enum TextCasing {
  UPPERCASE = 'UPPERCASE',
  STANDARD = 'STANDARD',
  /** Preserve the supplied casing while retaining the display/eyebrow tab typography. */
  AS_PROVIDED = 'AS_PROVIDED',
}
export type HorizontalTabsProps = {
  onPress: (tabId: string) => void
  selectedTabId: string
  tabs: { id: string; label: React.ReactNode; iconWithTooltip?: React.ReactNode; badgeCount?: number }[]
  testID?: string
  style?: ViewStyle
  tabStyle?: ViewStyle
  indicatorVariant?: IndicatorVariant
  textCasing?: TextCasing
  /** Override the pressed opacity for tab items (default: undefined, uses PressableOpacity default) */
  pressedOpacity?: number
  /** Override the active indicator height (default: '4px') */
  indicatorHeight?: string
  /** Override the active indicator border radius (default: theme.spacing['32px']) */
  indicatorBorderRadius?: string
  /** Override the active indicator background color */
  indicatorColor?: string
  /** Override the inactive tab text color */
  inactiveTextColor?: string
  /** Override the badge count color */
  badgeCountColor?: string
  /** Allow a single-row tab list to overflow horizontally. The selected tab is kept in view. */
  scrollable?: boolean
}

export const HorizontalTabs: FunctionComponent<HorizontalTabsProps> = ({
  tabs,
  onPress,
  selectedTabId,
  testID = 'HorizontalTabs',
  style,
  tabStyle,
  indicatorVariant = IndicatorVariant.UNDER_LABEL,
  textCasing = TextCasing.UPPERCASE,
  pressedOpacity,
  indicatorHeight,
  indicatorBorderRadius,
  indicatorColor,
  inactiveTextColor,
  badgeCountColor,
  scrollable = false,
}) => {
  const { colors } = useTheme()
  const scrollViewRef = useRef<ScrollView>(null)
  const scrollOffsetRef = useRef(0)
  const viewportWidthRef = useRef(0)
  const tabLayoutsRef = useRef(new Map<string, { x: number; width: number }>())

  const keepTabVisible = useCallback((tabId: string) => {
    const tabLayout = tabLayoutsRef.current.get(tabId)
    const viewportWidth = viewportWidthRef.current
    if (!tabLayout || viewportWidth <= 0) {
      return
    }

    const currentOffset = scrollOffsetRef.current
    const tabRight = tabLayout.x + tabLayout.width
    let nextOffset = currentOffset
    if (tabLayout.x < currentOffset) {
      nextOffset = tabLayout.x
    } else if (tabRight > currentOffset + viewportWidth) {
      nextOffset = tabRight - viewportWidth
    }

    if (nextOffset !== currentOffset) {
      scrollOffsetRef.current = nextOffset
      scrollViewRef.current?.scrollTo({ x: nextOffset, animated: false })
    }
  }, [])

  useEffect(() => {
    if (scrollable) {
      keepTabVisible(selectedTabId)
    }
  }, [keepTabVisible, scrollable, selectedTabId])

  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    scrollOffsetRef.current = event.nativeEvent.contentOffset.x
  }, [])

  const handleViewportLayout = useCallback(
    (event: LayoutChangeEvent) => {
      viewportWidthRef.current = event.nativeEvent.layout.width
      keepTabVisible(selectedTabId)
    },
    [keepTabVisible, selectedTabId],
  )

  const isPill = indicatorVariant === IndicatorVariant.PILL

  const tabRow = (
    <Row style={style} role='tablist' $isScrollable={scrollable} $isPill={isPill}>
      {tabs.map((tab) => {
        const isActive = tab.id === selectedTabId
        return (
          <TabWrapper
            key={tab.id}
            fullHeight={indicatorVariant === IndicatorVariant.BOTTOM_BAR}
            $preventShrink={scrollable}
            onLayout={
              scrollable
                ? (event) => {
                    const { x, width } = event.nativeEvent.layout
                    tabLayoutsRef.current.set(tab.id, { x, width })
                    if (isActive) {
                      keepTabVisible(tab.id)
                    }
                  }
                : undefined
            }
          >
            <TabItem
              onPress={() => onPress(tab.id)}
              testID={tID(`${testID}-tab-${tab.id}`)}
              role='tab'
              aria-selected={isActive}
              style={tabStyle}
              $preventShrink={scrollable}
              $isPill={isPill}
              $isActive={isActive}
              {...(pressedOpacity !== undefined && { pressedOpacity })}
            >
              {textCasing !== TextCasing.STANDARD && !isPill ? (
                <DisplayTabText
                  text={tab.label}
                  size={DisplaySize.EYEBROW}
                  isActive={isActive}
                  $preserveCase={textCasing === TextCasing.AS_PROVIDED}
                  numberOfLines={scrollable ? 1 : undefined}
                />
              ) : (
                <StandardTabText
                  text={tab.label}
                  size={BodyTextSize.DEFAULT}
                  isActive={isActive}
                  activeColor={colors.textPrimary}
                  inactiveColor={inactiveTextColor}
                  hasBadge={tab.badgeCount !== undefined && tab.badgeCount > 0}
                  $isPill={isPill}
                  numberOfLines={scrollable ? 1 : undefined}
                />
              )}

              {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                <TabBadge testID={tID(`${testID}-tab-${tab.id}-badge`)} badgeCountColor={badgeCountColor}>
                  <BodyText
                    text={String(tab.badgeCount)}
                    size={BodyTextSize.CAPTION}
                    color={colors.textInverse}
                    bold
                    textAlign='center'
                    style={{ fontSize: 12, lineHeight: 12 }}
                  />
                </TabBadge>
              )}

              {indicatorVariant === IndicatorVariant.UNDER_LABEL && isActive && (
                <ActiveIndicator
                  backgroundColor={indicatorColor}
                  height={indicatorHeight}
                  borderRadius={indicatorBorderRadius}
                />
              )}
            </TabItem>

            {indicatorVariant === IndicatorVariant.BOTTOM_BAR && isActive && (
              <ActiveIndicator
                backgroundColor={indicatorColor || colors.textSecondary}
                height={indicatorHeight}
                borderRadius={indicatorBorderRadius}
              />
            )}

            {tab?.iconWithTooltip}
          </TabWrapper>
        )
      })}
    </Row>
  )

  if (!scrollable) {
    return tabRow
  }

  return (
    <ScrollView
      ref={scrollViewRef}
      horizontal
      bounces={false}
      showsHorizontalScrollIndicator={false}
      onLayout={handleViewportLayout}
      onScroll={handleScroll}
      scrollEventThrottle={16}
      testID={tID(`${testID}-scroll`)}
      style={{ width: '100%' }}
    >
      {tabRow}
    </ScrollView>
  )
}
